import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import TableComponent from '../TableComponent';
import EnvironmentTableFields from './TableField/Environment';
import {
  getEnvironmentVariableList,
  updateEnvironmentVariable,
} from '../../utils/api/Variable_apis';
import {
  fetchEnvironmentList,
  createEnvironment,
  updateEnvironment,
  deleteEnvironment,
} from '../../utils/api/environment_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useSnackbar } from '../../utils/useSnackbar';
import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import AddEnvironmentPopup from './Popup/AddUpdateEnvironmentPopup';
import UpdateEnvironmentVariableSlider from './Popup/UpdateEnvironmentVariableSlider';
import { Box } from '@mui/material';
import ConfirmationModal from '../Modal/ConfirmationModal';
import { getDeleteModalTitle, getDeleteConfirmMessage } from '../../utils/uiText';

const TABLE_TITLE = 'Environment';

export default function EnvironmentComponent() {
  const [loading, setLoading] = useState(false);
  const [environmentList, setEnvironmentList] = useState([]);
  const [popupType, setPopupType] = useState(null);
  const [popupData, setPopupData] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const { showSnackbar } = useSnackbar();
  
  // Keep a ref to selectedRows for async actions
  const selectedRowsRef = useRef(selectedRows);
  useEffect(() => {
    selectedRowsRef.current = selectedRows;
  }, [selectedRows]);

  // Fetch environments on mount
  useEffect(() => {
    let isMounted = true;
    const fetchEnvironments = async () => {
      setLoading(true);
      try {
        const { data = [], status, message, error } = await fetchEnvironmentList();
        if (!isMounted) return;
        if (status === 1) {
          setEnvironmentList(data ?? []);
          // showSnackbar(message || 'Environment list loaded.', 'success');
        } 
        // else {
        //   showSnackbar(error || 'Failed to fetch environment list.', 'error');
        // }
      } catch {
        if (isMounted) showSnackbar('Unexpected error fetching environment list.', 'error');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchEnvironments();
    return () => {
      isMounted = false;
    };
  }, []);

  const togglePopup = useCallback((type, data = null) => {
    setPopupType(type);
    setPopupData(data);
  }, []);

  const closePopup = useCallback(() => {
    setPopupType(null);
    setPopupData(null);
  }, []);

  const handleEnvironmentAdd = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await createEnvironment({
          name: data.environmentName,
          description: data.environmentDescription,
        });
        if (response?.status === 1) {
          setEnvironmentList((prev) => [...prev, response.data]);
          closePopup();
          showSnackbar(response?.message || 'Environment created successfully.', 'success');
        } else {
          showSnackbar(response?.error || 'Failed to create environment.', 'error');
        }
      } finally {
        setLoading(false);
      }
    },
    [closePopup, showSnackbar]
  );

  const handleEnvironmentUpdate = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await updateEnvironment({
          id: popupData?.id,
          data: {
            name: data?.environmentName,
            description: data?.environmentDescription,
          },
        });
        if (response?.status === 1) {
          setEnvironmentList((envs) =>
            envs.map((environment) =>
              environment.id === popupData?.id ? response.data : environment
            )
          );
          closePopup();
          showSnackbar(response?.message || 'Environment updated successfully.', 'success');
        } else {
          showSnackbar(response?.error || 'Failed to update environment.', 'error');
        }
      } finally {
        setLoading(false);
      }
    },
    [popupData, closePopup, showSnackbar]
  );


  const handleEnvironmentVariableUpdate = useCallback(
    async (data, environmentId) => {
      setLoading(true);
      const variableArray = [];
      for (let key in data) {
        variableArray.push({
          id: key,
          value: data[key].value,
          isSecret: data[key].encrypted
        });
      }
      const response = await updateEnvironmentVariable({variableArray, environmentId});
      if(response.status === STATUS.SUCCESS){
        showSnackbar(response.message || 'Environment variable updated successfully.', 'success');
        setLoading(false);
        closePopup();
      } else {
        showSnackbar(response.error || 'Failed to update environment variable.', 'error');
        setLoading(false);
      }
    },
    [popupData, closePopup, showSnackbar]
  );

  const handleDeleteEnvironments = useCallback(
    async (ids = []) => {
      if (!ids.length) return;
      setLoading(true);
      try {
        const response = await deleteEnvironment({ environmentIds: ids });
        if (response?.status === 1) {
          setEnvironmentList((envs) => envs.filter((env) => !ids.includes(env.id)));
          setSelectedRows([]);
          closePopup();
          showSnackbar(response?.message || 'Environment deleted successfully.', 'success');
        } else {
          showSnackbar(response?.error || 'Failed to delete environment.', 'error');
        }
      } finally {
        setLoading(false);
      }
    },
    [showSnackbar]
  );

  // Header action buttons
  const headerActionButtons = useMemo(
    () => [
      {
        name: 'Add',
        tooltip: 'Add New Environment',
        icon: <PlusIcon className='icon' />,
        action: () => togglePopup('add_environment'),
        // disabled: loading,
      },
      {
        name: 'Delete',
        tooltip: 'Delete Selected Environment(s)',
        icon: <TrashIcon className='icon' />,
        action: () => togglePopup('delete_confirmation', selectedRowsRef.current),
        disabled: loading || !selectedRows.length,
      },
    ],
    [loading, togglePopup, handleDeleteEnvironments, selectedRows]
  );

  // Row action buttons
  const rowActionButtons = useMemo(
    () => [
      {
        name: 'Edit',
        tooltip: 'Edit',
        icon: <PencilIcon className='icon' />,
        action: (row) => togglePopup('edit_environment', row),
        disabled: false,
      },
      {
        name: 'Delete',
        tooltip: 'Delete',
        icon: <TrashIcon className='icon' />,
        action: (row) => togglePopup('delete_confirmation', [row.id]),
          //handleDeleteEnvironments([row.id]),
        disabled: false,
      },
    ],
    [togglePopup, handleDeleteEnvironments]
  );

  // Render popup based on type
  const RenderPopup = useCallback(() => {
    if (popupType === 'add_environment') {
      return (
        <AddEnvironmentPopup
          closePopup={closePopup}
          handleEnvironmentAdd={handleEnvironmentAdd}
          type="add"
        />
      );
    }
    if (popupType === 'edit_environment') {
      return (
        <AddEnvironmentPopup
          closePopup={closePopup}
          initialValues={{
            environmentName: popupData?.name,
            environmentDescription: popupData?.description,
          }}
          handleEnvironmentUpdate={handleEnvironmentUpdate}
          type="edit"
        />
      );
    }
    if (popupType === 'update_environment_variable') {
      return (
        <UpdateEnvironmentVariableSlider
          open={true}
          closePopup={closePopup}
          popupData={popupData}
          handleEnvironmentVariableUpdate={handleEnvironmentVariableUpdate}
          loading={loading}
        />
      );
    }
    if (popupType === 'delete_confirmation') {
      return (
        <ConfirmationModal
          open={true}
          onClose={closePopup}
          onConfirm={() => handleDeleteEnvironments(popupData)}
          title={getDeleteModalTitle()}
          message={getDeleteConfirmMessage('environment', popupData?.length, popupData?.length > 1, 'It will permanently remove all variables associated with it.')}
          loading={loading}
        />
      );
    }
    return null;
  }, [popupType, closePopup, handleEnvironmentAdd, handleEnvironmentUpdate, popupData, handleEnvironmentVariableUpdate, handleDeleteEnvironments, loading]);

  const handleRowClick = useCallback(async (params) => {
    const response = await getEnvironmentVariableList({environmentId: params.id});
    if(response.status === STATUS.SUCCESS){
      const data = {
        environmentId: params.id,
        environmentName: response.data?.environmentName,
        environmentVariables: response.data?.variables,
      }
      togglePopup('update_environment_variable', data);
    } else {
      showSnackbar(response.error || 'Failed to fetch environment variables.', 'error');
    }
  }, [togglePopup]);

  return (
    <>
      <TableComponent
        viewType="environment"
        title={TABLE_TITLE}
        hideColumnsVisibilityButton
        headerActionButtons={headerActionButtons}
        rows={environmentList}
        columns={EnvironmentTableFields(rowActionButtons)}
        isCheckbox
        loading={loading}
        idField="id"
        isColumnResizable={!loading}
        isRowSelection
        handleRowSelection={setSelectedRows}
        selectedRows={selectedRows}
        handleRowClick={handleRowClick}
      />
      {RenderPopup()}
    </>
  );
}